// Auto-generated lightweight class for Questionnaire
// DO NOT EDIT - Changes will be overwritten

import { NeoNodeBase } from '@sourcepilot/framework/neo-node-base';
import { RelationshipManager } from '@sourcepilot/framework/relationship-manager';
import { Relationship } from '@sourcepilot/framework/relationship';
import { QuestionnaireReadAPI, QuestionnaireWriteAPI, QuestionnaireReadAPISchema, QuestionnaireWriteAPISchema } from '../types/questionnaire';
import { ModelRegistry } from '../../registries/model-registry';
import { AtScope, HasQuestionnaire, HasQuestions } from '../../relationships/relationship';

import { Question } from './question';
import { SecurityScope } from '../../shared/api/securityscope';
import { ToolCallPart } from '../../chat/api/toolcallpart';



export interface Questionnaire extends Partial<QuestionnaireReadAPI> {}
export class Questionnaire extends NeoNodeBase<QuestionnaireReadAPI, QuestionnaireWriteAPI> {
  // Static label for identifying this model type (minification-safe)
  static readonly label = "Questionnaire";
  static readonly readSchema = QuestionnaireReadAPISchema;
  static readonly writeSchema = QuestionnaireWriteAPISchema;

  // Per-relationship metadata, keyed by property name on this class.
  // See `framework/data/dag-sync/reverse-property.ts` for how the DAG
  // client uses (relClass, direction) to route patches back to the
  // correct property — especially for polymorphic-source rels.
  static readonly relationships: Record<string, {
    target: string;
    cardinality: 'one' | 'many';
    isParent: boolean;
    relClass: string;
    direction: 'in' | 'out';
  }> = {
    tool_call_part: { target: "ToolCallPart", cardinality: "one", isParent: true, relClass: "HasQuestionnaire", direction: "in" },
    questions: { target: "Question", cardinality: "many", isParent: false, relClass: "HasQuestions", direction: "out" },
  };

  protected get modelName(): string {
    return 'Questionnaire';
  }

  // Instance getter for label (used in DAG sync and type identification)
  get _label(): string {
    return Questionnaire.label;
  }


  constructor(data: Partial<QuestionnaireWriteAPI> = {}) {
    super(data);
  }

/**
   * Load instance from server data with Zod validation
   * Recursively instantiates nested relationship objects
   *
   * @param data - Node data from API (ReadAPI format)
   * @param loadContext - Optional LoadContext from backend QueryResult for eager-loaded relationships
   * @returns Class instance with nested relationships instantiated
   */
  static load(data: QuestionnaireReadAPI, loadContext?: Record<string, any>): Questionnaire {
    // Import relationship loaders
    const { loadOneRelationship, loadManyRelationship } = require('@sourcepilot/framework/load-relationship');

    // Strip relationship fields before validation (they'll be processed separately)
    const { questions, security_scope, tool_call_part, _label, ...dataToValidate } = data as any;

    // Validate with Zod schema (only non-relationship fields)
    const validated = QuestionnaireReadAPISchema.parse(dataToValidate);

    // Re-add relationship fields to validated data for processing
    const dataWithRelationships: any = { ...validated, questions: questions, security_scope: security_scope, tool_call_part: tool_call_part };

    // If loadContext provided, use NeoGraphParser to reconstruct relationships
    if (loadContext && validated.id) {
      // Import NeoGraphParser dynamically to avoid circular dependencies
      const { NeoGraphParser } = require('@sourcepilot/framework/neograph-utils');
      const parser = new NeoGraphParser();
      const graphNodes = parser.parse([validated], loadContext);

      if (graphNodes.length > 0) {
        // Convert graph structure back to plain object with relationships
        const withRelationships = parser.toPlainObject(graphNodes[0]);
        // Recursively instantiate via ModelRegistry (without loadContext to avoid infinite loop)
        return Questionnaire.load(withRelationships);
      }
    }

    // Recursively instantiate nested relationships from inline data
    const processed: any = { ...dataWithRelationships };

    // Process relationships: convert to class instances and store with private property names
    loadManyRelationship(processed, 'questions', 'Question', ModelRegistry);
    loadOneRelationship(processed, 'security_scope', 'SecurityScope', ModelRegistry);
    loadOneRelationship(processed, 'tool_call_part', 'ToolCallPart', ModelRegistry);
    // Create instance with processed data (constructor uses Object.assign)
    // Private properties (_*) won't conflict with relationship getters
    const instance = new Questionnaire(processed as any);

    return instance;
  }


  // Relationship manager for 'questions' - Python ORM style (cached)
  get questions(): RelationshipManager<this, Question> {
    return this.getOrCreateRelationshipManager('questions', () => {
      return new RelationshipManager(this, 'questions', 'many', HasQuestions as typeof Relationship);
    });
  }

  // Relationship manager for 'security_scope' - Python ORM style (cached)
  get security_scope(): RelationshipManager<this, SecurityScope> {
    return this.getOrCreateRelationshipManager('security_scope', () => {
      return new RelationshipManager(this, 'security_scope', 'one', AtScope as typeof Relationship);
    });
  }

  // Relationship manager for 'tool_call_part' - Python ORM style (cached)
  get tool_call_part(): RelationshipManager<this, ToolCallPart> {
    return this.getOrCreateRelationshipManager('tool_call_part', () => {
      return new RelationshipManager(this, 'tool_call_part', 'one', HasQuestionnaire as typeof Relationship);
    });
  }

}
